import { FC, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { AppButton, AppInput, FigmaTypography } from "@libs/zapi-ui/custom";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@libs/zapi-ui/shadcn";
import $api from "../../shared/api/axios";
import { AppLoader } from "../../shared/components/AppLoader";
import { getRoutePromptsFeature, getRoutePromptsRevision } from "../../shared/const/router";
import { DeficiencyDialog } from "../../shared/components/DeficiencyDialog/DeficiencyDialog";
import { PromptEditor } from "./PromptEditor";
import { SkillFolderEditor } from "./SkillFolderEditor";
import { RevisionTests } from "./RevisionTests";
import {
  DEFAULT_GENERATION_MODEL,
  GENERATION_MODELS,
  GENERATION_MODEL_HINTS,
  SKILL_FOLDER_PROMPT_KEYS,
  STATUS_STYLES,
  TGenerationModel,
  TRebasePlan,
  TRevisionDetail,
  formatTimestamp,
  hasLintErrors
} from "./revision";
import { PromptFlagBadge } from "./PromptFlagBadge";

const RebaseBanner: FC<{ plan: TRebasePlan; onRecreate: () => void; isRecreating: boolean }> = ({
  plan,
  onRecreate,
  isRecreating
}) => {
  if (!plan.needed) return null;

  const activeNumber = plan.activeRevisionNumber ?? plan.activeRevisionId;

  return (
    <div className="rounded-lg border border-amber-200 bg-amber-50 px-6 py-4 text-amber-800">
      <FigmaTypography component="div" variant="body-2">
        Revision {activeNumber} became active after this draft was cut, so this draft is stale and cannot be
        activated.
      </FigmaTypography>
      <div className="mt-1 space-y-0.5 text-[12px] leading-[18px]">
        <div>
          Discard this draft and start a new one from revision {activeNumber} (the latest active revision)
          instead.
        </div>
        {plan.conflicting.length > 0 && <div>Changed on both sides: {plan.conflicting.join(", ")}</div>}
        {plan.incoming.length > 0 && <div>Newer text in the active revision: {plan.incoming.join(", ")}</div>}
      </div>
      <div className="mt-3">
        <AppButton onClick={onRecreate} isLoading={isRecreating} loadingText="Starting...">
          Discard &amp; start from latest
        </AppButton>
      </div>
    </div>
  );
};

type TActivationResponse = {
  revisionId: number;
  revisionNumber: number | null;
  deactivatedRevisionId: number | null;
  deactivatedRevisionNumber: number | null;
};

export const RevisionPage: FC = () => {
  const { featureKey = "", revisionId = "" } = useParams<{ featureKey: string; revisionId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [note, setNote] = useState("");
  const [deficiencyOpen, setDeficiencyOpen] = useState(false);

  const revisionKey = ["admin-prompt-revision", revisionId];

  const { data: revision, isLoading, isError } = useQuery<TRevisionDetail>({
    queryKey: revisionKey,
    queryFn: async () => {
      const response = await $api.get(`/admin/prompts/revisions/${revisionId}`);
      return response.data;
    },
    enabled: !!revisionId
  });

  useEffect(() => {
    setNote(revision?.note ?? "");
  }, [revision?.note]);

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: revisionKey });
    queryClient.invalidateQueries({ queryKey: ["admin-prompt-revisions", featureKey] });
    queryClient.invalidateQueries({ queryKey: ["admin-prompts", featureKey] });
    queryClient.invalidateQueries({ queryKey: ["admin-prompt-features"] });
  };

  const saveModel = useMutation({
    mutationFn: (model: TGenerationModel) => $api.patch(`/admin/prompts/revisions/${revisionId}/model`, { model }),
    onSuccess: () => {
      toast.success("Model saved");
      invalidate();
    },
    onError: (error: { response?: { data?: { message?: string } } }) =>
      toast.error(error?.response?.data?.message ?? "Failed to save the model")
  });

  const saveNote = useMutation({
    mutationFn: () => $api.patch(`/admin/prompts/revisions/${revisionId}`, { note: note.trim() || null }),
    onSuccess: () => {
      toast.success("Note saved");
      invalidate();
    },
    onError: () => toast.error("Failed to save the note")
  });

  const activate = useMutation({
    mutationFn: async () => {
      const response = await $api.post(`/admin/prompts/revisions/${revisionId}/activate`);
      return response.data as TActivationResponse;
    },
    onSuccess: (data) => {
      toast.success(
        data.deactivatedRevisionId
          ? `Revision ${data.revisionNumber ?? data.revisionId} is live; ${data.deactivatedRevisionNumber ?? data.deactivatedRevisionId} is now inactive`
          : `Revision ${data.revisionNumber ?? data.revisionId} is live`
      );
      invalidate();
    },
    onError: (error: { response?: { data?: { message?: string } } }) =>
      toast.error(error?.response?.data?.message ?? "Failed to activate the revision")
  });

  const reactivate = useMutation({
    mutationFn: async () => {
      const response = await $api.post(`/admin/prompts/revisions/${revisionId}/reactivate`);
      return response.data as TActivationResponse;
    },
    onSuccess: (data) => {
      toast.success(
        data.deactivatedRevisionId
          ? `Revision ${data.revisionNumber ?? data.revisionId} is live again; ${data.deactivatedRevisionNumber ?? data.deactivatedRevisionId} is now inactive`
          : `Revision ${data.revisionNumber ?? data.revisionId} is live again`
      );
      invalidate();
    },
    onError: (error: { response?: { data?: { message?: string } } }) =>
      toast.error(error?.response?.data?.message ?? "Failed to activate the revision")
  });

  const pin = useMutation({
    mutationFn: () => $api.post(`/admin/prompts/revisions/${revisionId}/pin`),
    onSuccess: () => {
      toast.success(`Your ${featureKey} generations now use revision ${revision?.revisionNumber ?? revisionId}`);
      invalidate();
    },
    onError: (error: { response?: { data?: { message?: string } } }) =>
      toast.error(error?.response?.data?.message ?? "Failed to use this revision")
  });

  const unpin = useMutation({
    mutationFn: () => $api.post(`/admin/prompts/features/${featureKey}/unpin`),
    onSuccess: () => {
      toast.success(`Back to the active ${featureKey} revision`);
      invalidate();
    },
    onError: () => toast.error("Failed to stop using this revision")
  });

  const discard = useMutation({
    mutationFn: () => $api.post(`/admin/prompts/revisions/${revisionId}/discard`),
    onSuccess: () => {
      toast.success("Draft discarded");
      invalidate();
      navigate(getRoutePromptsFeature(featureKey));
    },
    onError: () => toast.error("Failed to discard the draft")
  });

  const recreateFromLatest = useMutation({
    mutationFn: async () => {
      const body = revision?.deficiencyId
        ? { deficiencyId: revision.deficiencyId, note: `Cure deficiency #${revision.deficiencyId}` }
        : { note: revision?.note?.trim() || "Recreated from the latest active revision" };
      const created = await $api.post(`/admin/prompts/features/${featureKey}/revisions`, body);
      await $api.post(`/admin/prompts/revisions/${revisionId}/discard`);
      return created.data as { revisionId: number; revisionNumber: number };
    },
    onSuccess: (data) => {
      toast.success(
        `Started revision ${data.revisionNumber} from the latest active revision; discarded ${revision?.revisionNumber}`
      );
      invalidate();
      navigate(getRoutePromptsRevision(featureKey, data.revisionId));
    },
    onError: (error: { response?: { data?: { message?: string } } }) =>
      toast.error(error?.response?.data?.message ?? "Failed to recreate the draft")
  });

  if (isLoading) return <AppLoader />;
  if (isError || !revision) {
    return <div className="p-8 text-center text-red-600">Failed to load the revision.</div>;
  }

  const isDraft = revision.status === "draft";
  const selectedModel = GENERATION_MODELS.find((model) => model === revision.model) ?? DEFAULT_GENERATION_MODEL;
  const isInactive = revision.status === "inactive";
  const lintBlocked = revision.entries.some((entry) => hasLintErrors(entry.lint));
  const rebaseBlocked = Boolean(revision.rebase?.needed);
  const testsBlocked = revision.passedTestCaseCount < revision.requiredPassedTestCaseCount;
  const pinnedHere = revision.pinnedRevisionId === revision.id;
  const canPin = revision.status === "draft" || revision.status === "inactive";

  return (
    <div className="container mx-auto py-8 max-w-4xl">
      <div className="mb-6">
        <Link to={getRoutePromptsFeature(featureKey)} className="text-[14px] text-gray-500 hover:text-gray-700">
          ← Back to {featureKey}
        </Link>
        <div className="mt-2 flex items-center gap-3">
          <FigmaTypography component="h1" variant="heading-1">
            Revision {revision.revisionNumber ?? revision.id}
          </FigmaTypography>
          <span className={`rounded px-2 py-0.5 text-[12px] leading-[18px] ${STATUS_STYLES[revision.status]}`}>
            {revision.status}
          </span>
          {pinnedHere && (
            <span className="rounded bg-purple-100 px-2 py-0.5 text-[12px] leading-[18px] text-purple-800">
              you are testing this
            </span>
          )}
          {revision.deficiencyId && (
            <button
              type="button"
              onClick={() => setDeficiencyOpen(true)}
              className="rounded bg-amber-50 px-2 py-0.5 text-[12px] leading-[18px] text-amber-700 hover:bg-amber-100"
            >
              curing deficiency #{revision.deficiencyId}
            </button>
          )}
        </div>
        <FigmaTypography component="div" variant="body-3" className="text-gray-500 mt-1">
          Created {formatTimestamp(revision.createdAt)} by {revision.createdByName ?? "the code registry"}
          {revision.parentRevisionId
            ? ` · branched from revision ${revision.parentRevisionNumber ?? revision.parentRevisionId}`
            : ""}
          {revision.activatedAt ? ` · activated ${formatTimestamp(revision.activatedAt)} by ${revision.activatedByName}` : ""}
          {revision.supportsModel ? ` · model ${revision.model ?? `${DEFAULT_GENERATION_MODEL} (default)`}` : ""}
        </FigmaTypography>
      </div>

      <div className="space-y-6">
        {revision.rebase && (
          <RebaseBanner
            plan={revision.rebase}
            onRecreate={() => recreateFromLatest.mutate()}
            isRecreating={recreateFromLatest.isPending}
          />
        )}

        {isDraft && !revision.deficiencyId && (
          <div className="rounded-lg border border-amber-200 bg-amber-50 px-6 py-3 text-[12px] leading-[18px] text-amber-800">
            No deficiency linked — this draft is a maintenance change, and the note below is the only record of why
            the prompts changed.
          </div>
        )}

        {revision.keysResolvedFromDefault.length > 0 && (
          <div className="rounded-lg border border-gray-200 bg-gray-50 px-6 py-3 text-[12px] leading-[18px] text-gray-600">
            Registered in code but absent here, so they resolve from the code default:{" "}
            {revision.keysResolvedFromDefault.join(", ")}
          </div>
        )}

        <div className="rounded-lg border bg-card px-6 py-4">
          <FigmaTypography component="div" variant="body-3" className="text-gray-500 mb-2">
            Note
          </FigmaTypography>
          {isDraft ? (
            <div className="flex items-center gap-2">
              <AppInput
                value={note}
                onChange={(event) => setNote(event.target.value)}
                placeholder="Why this revision exists"
                className="flex-1"
              />
              <AppButton
                appearance="outlined"
                onClick={() => saveNote.mutate()}
                disabled={note.trim() === (revision.note ?? "").trim()}
                isLoading={saveNote.isPending}
              >
                Save note
              </AppButton>
            </div>
          ) : (
            <FigmaTypography component="div" variant="body-2" className="text-gray-800">
              {revision.note || "—"}
            </FigmaTypography>
          )}
        </div>

        {revision.supportsModel && (
          <div className="rounded-lg border bg-card px-6 py-4">
            <FigmaTypography component="div" variant="body-3" className="text-gray-500 mb-2">
              Model
            </FigmaTypography>
            <FigmaTypography component="div" variant="body-3" className="text-gray-500 mb-3">
              Which Claude model the runner uses for this revision's runs. Opus costs many times what sonnet
              does — activating a revision switches the model with it.
            </FigmaTypography>
            {isDraft ? (
              <div className="flex items-center gap-3">
                <Select
                  value={selectedModel}
                  onValueChange={(value) => saveModel.mutate(value as TGenerationModel)}
                  disabled={saveModel.isPending}
                >
                  <SelectTrigger className="w-[180px] h-9 text-[14px] bg-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {GENERATION_MODELS.map((model) => (
                      <SelectItem key={model} value={model} className="text-[14px]">
                        {model}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FigmaTypography component="div" variant="body-3" className="text-gray-500">
                  {GENERATION_MODEL_HINTS[selectedModel]}
                </FigmaTypography>
              </div>
            ) : (
              <div className="flex items-center gap-3">
                <FigmaTypography component="div" variant="body-2" className="text-gray-800">
                  {revision.model ?? `${DEFAULT_GENERATION_MODEL} (default)`}
                </FigmaTypography>
                <FigmaTypography component="div" variant="body-3" className="text-gray-500">
                  {GENERATION_MODEL_HINTS[selectedModel]}
                </FigmaTypography>
              </div>
            )}
          </div>
        )}

        {revision.supportsTests && (
          <RevisionTests
            featureKey={featureKey}
            revisionId={revision.id}
            isActive={revision.status === "active"}
            passedTestCaseCount={revision.passedTestCaseCount}
            requiredPassedTestCaseCount={revision.requiredPassedTestCaseCount}
            onVerdictsChanged={invalidate}
          />
        )}

        {canPin && (
          <div className="flex items-start gap-3 rounded-lg border bg-card px-6 py-4">
            {pinnedHere ? (
              <AppButton
                appearance="outlined"
                onClick={() => unpin.mutate()}
                isLoading={unpin.isPending}
                loadingText="Stopping..."
              >
                Stop using it
              </AppButton>
            ) : (
              <AppButton onClick={() => pin.mutate()} isLoading={pin.isPending} loadingText="Switching...">
                Use for your account
              </AppButton>
            )}
            <div className="text-[12px] leading-[18px] text-gray-500">
              {pinnedHere ? (
                <span>
                  Your own generations run on this revision; everyone else stays on the active one. Reversible anytime
                  with &ldquo;Stop using it&rdquo;.
                </span>
              ) : (
                <div className="space-y-1.5">
                  <div className="font-medium text-gray-700">
                    {revision.pinnedRevisionNumber
                      ? `You are currently testing revision ${revision.pinnedRevisionNumber}. Using this one for your account replaces that.`
                      : "Test this revision on your own account before activating it for everyone."}
                  </div>
                  <ul className="list-disc space-y-0.5 pl-4 text-gray-400">
                    <li>
                      Only <span className="font-medium text-gray-500">your</span> generations use these prompts —
                      everyone else keeps running on the active revision.
                    </li>
                    <li>Nothing goes live: the active revision stays unchanged until you activate.</li>
                    <li>
                      Applies to new generations you start, until you stop or this revision is activated or discarded.
                    </li>
                    <li>Reversible anytime — &ldquo;Stop using it&rdquo; puts you back on the active revision.</li>
                  </ul>
                </div>
              )}
            </div>
          </div>
        )}

        {isDraft && (
          <div className="flex items-center gap-3 rounded-lg border bg-card px-6 py-4">
            <AppButton
              color="green500"
              onClick={() => activate.mutate()}
              disabled={lintBlocked || rebaseBlocked || testsBlocked}
              isLoading={activate.isPending}
              loadingText="Activating..."
            >
              Activate
            </AppButton>
            <AppButton
              appearance="outlined"
              color="red400"
              onClick={() => discard.mutate()}
              isLoading={discard.isPending}
              loadingText="Discarding..."
            >
              Discard draft
            </AppButton>
            <FigmaTypography component="div" variant="body-3" className="text-gray-500">
              {rebaseBlocked
                ? "A newer revision is active — discard this draft and start from the latest (see above)."
                : lintBlocked
                  ? "Fix the placeholder errors first."
                  : testsBlocked
                    ? `${revision.passedTestCaseCount} of ${revision.requiredPassedTestCaseCount} test cases passed — run more before activating.`
                    : "Activating replaces the live prompts for this feature."}
            </FigmaTypography>
          </div>
        )}

        {isInactive && (
          <div className="flex items-center gap-3 rounded-lg border bg-card px-6 py-4">
            <AppButton
              color="green500"
              onClick={() => reactivate.mutate()}
              isLoading={reactivate.isPending}
              loadingText="Activating..."
            >
              Make active
            </AppButton>
            <FigmaTypography component="div" variant="body-3" className="text-gray-500">
              Roll back to this revision — it replaces the live prompts for the whole platform. The current active
              revision becomes inactive and can be restored the same way.
            </FigmaTypography>
          </div>
        )}

        {revision.entries.map((entry) =>
          SKILL_FOLDER_PROMPT_KEYS[featureKey] === entry.promptKey ? (
            <SkillFolderEditor
              key={entry.promptKey}
              revisionId={revision.id}
              revisionLabel={revision.revisionNumber ?? revision.id}
              entry={entry}
              isDraft={isDraft}
              onSaved={invalidate}
            />
          ) : isDraft ? (
            <PromptEditor
              key={entry.promptKey}
              featureKey={featureKey}
              revisionId={revision.id}
              entry={entry}
              onSaved={invalidate}
            />
          ) : (
            <div key={entry.promptKey} className="rounded-lg border bg-card shadow-xs overflow-hidden">
              <div className="px-6 py-4 border-b bg-gray-50 flex items-start justify-between gap-4">
                <div className="min-w-0">
                  <div className="text-[14px] font-medium text-gray-900">{entry.label}</div>
                  <FigmaTypography component="div" variant="body-3" className="text-gray-400 mt-0.5">
                    {entry.promptKey}
                  </FigmaTypography>
                </div>
                <div className="flex shrink-0 flex-col items-end gap-1">
                  <PromptFlagBadge featureFlag={entry.featureFlag} />
                  {entry.differsFromActive && (
                    <span className="rounded bg-amber-100 px-2 py-0.5 text-[12px] leading-[18px] text-amber-800">
                      Differs from active revision
                    </span>
                  )}
                </div>
              </div>
              <pre className="max-h-[600px] overflow-auto px-6 py-4 whitespace-pre-wrap font-mono text-[12px] leading-[18px] text-gray-800">
                {entry.content}
              </pre>
            </div>
          )
        )}

        {revision.codeDefaultPrompts.map((prompt) => (
          <div key={prompt.promptKey} className="rounded-lg border border-dashed bg-card shadow-sm overflow-hidden">
            <div className="px-6 py-4 border-b bg-gray-50 flex items-start justify-between gap-4">
              <div className="min-w-0">
                <div className="text-[14px] font-medium text-gray-900">{prompt.label}</div>
                <FigmaTypography component="div" variant="body-3" className="text-gray-400 mt-0.5">
                  {prompt.promptKey}
                </FigmaTypography>
                <FigmaTypography component="div" variant="body-3" className="text-gray-500 mt-2">
                  Not stored in this revision, so it uses the text in the code. A new draft stores it.
                </FigmaTypography>
              </div>
              <div className="flex shrink-0 flex-col items-end gap-1">
                <PromptFlagBadge featureFlag={prompt.featureFlag} />
                <span className="rounded bg-gray-100 px-2 py-0.5 text-[12px] leading-[18px] text-gray-700">Code default</span>
              </div>
            </div>
            <pre className="max-h-[600px] overflow-auto px-6 py-4 whitespace-pre-wrap font-mono text-[12px] leading-[18px] text-gray-800">
              {prompt.content}
            </pre>
          </div>
        ))}
      </div>

      <DeficiencyDialog
        deficiencyId={deficiencyOpen ? revision.deficiencyId : null}
        onClose={() => setDeficiencyOpen(false)}
      />
    </div>
  );
};
